<template>
    <b-container fluid>
        <h1 class="text-left">
            {{ title }}
            <em
                v-show="
                    editThemeLayer && editThemeLayer.unavailable && !isOGCAPI
                "
                class="error fas fa-exclamation-triangle"
                :title="$t('themelayerNotAvailable')"
            >
            </em>
        </h1>
        <br />
        <b-form
            ref="themeLayerForm"
            class="col-lg-6 extra-padding-bottom"
            name="themeLayerForm"
            v-on:submit.prevent="$_onSubmit"
        >
            <!-- OWS -->
            <x-select
                :id="'ows-service-id'"
                :name="'ows-service-name'"
                :text="$t('labelOwsAvailable')"
                :value="owsServicesId"
                :options="comboBoxOwsServices"
                :disabled="isEditMode"
                required
                :extendedClass="{ plaintext: isEditMode }"
                @selected="$_onOwsSelected"
            >
            </x-select>
            <div id="convert-button-container" v-if="showLayerConvertButton">
                <b-button
                    :id="'convert-themelayer'"
                    :name="'convert-themelayer'"
                    variant="danger"
                    size="sm"
                    @click="$_onConversionToNedGlobeServices"
                >
                    {{ $t('labelConvertThemeLayer') }}
                </b-button>
            </div>
            <!-- Layername -->
            <b-form-group
                v-show="
                    selectedOwsService && !isOwsNedGlobeServices && !isOGCAPI
                "
                id="fieldset-2"
                :label="$t('labelLayer')"
                label-class="font-weight-normal"
                :label-for="'ows-layer-name'"
            >
                <b-form-select
                    :id="'ows-layer-id'"
                    :name="'ows-layer-name'"
                    :value="owsLayerValue"
                    :disabled="disableLayerName"
                    :state="stateValues.nameValidState"
                    :class="{ plaintext: disableLayerName }"
                    :required="isLayerNameRequired"
                    @input="$_onLayerSelected"
                >
                    <template v-slot:first>
                        <option :value="null">
                            {{ $t('selectBoxMakeChoice') }}
                        </option>
                    </template>
                    <template v-if="comboBoxOwsServiceWfsLayers.length > 0">
                        <optgroup label="WFS">
                            <option
                                v-for="item in comboBoxOwsServiceWfsLayers"
                                v-bind:value="item.value"
                                v-bind:key="item.text"
                            >
                                {{ item.text }}
                            </option>
                        </optgroup>
                    </template>
                    <template v-if="comboBoxOwsServiceWmsLayers.length > 0">
                        <optgroup label="WMS">
                            <option
                                v-for="item in comboBoxOwsServiceWmsLayers"
                                v-bind:value="item.value"
                                v-bind:key="item.text"
                            >
                                {{ item.text }}
                            </option>
                        </optgroup>
                    </template>
                    <template v-if="comboBoxOwsServiceWmtsLayers.length > 0">
                        <optgroup label="WMTS">
                            <option
                                v-for="item in comboBoxOwsServiceWmtsLayers"
                                v-bind:value="item.value"
                                v-bind:key="item.text"
                            >
                                {{ item.text }}
                            </option>
                        </optgroup>
                    </template>
                </b-form-select>
                <label
                    class="error small"
                    v-show="!stateValues.nameValidState"
                    >{{ stateValues.nameInvalidMessage }}</label
                >
            </b-form-group>
            <b-alert
                v-show="owsLayerNameMessage.length > 0"
                show
                variant="danger"
            >
                {{ owsLayerNameMessage }}
            </b-alert>
            <div v-if="isTransactionalWfs" class="label-transactional">
                {{ $t('labelWfsIsTransactional') }}
            </div>
            <!--Tabs-->
            <b-tabs
                v-show="selectedOwsService"
                content-class="mt-3"
                active-nav-item-class="text-primary"
                :class="{ 'hide-tabs': !canHaveStyles }"
            >
                <!--General-->
                <b-tab :title="$t('labelGeneral')" active>
                    <div class="col-lg-10">
                        <!-- NedGlobe Services -->
                        <div
                            v-show="selectedOwsService && isOwsNedGlobeServices"
                        >
                            <!-- Selection source (luchtfoto/database) -->
                            <x-select
                                id="selection-source-id"
                                name="selection-source-name"
                                :text="$t('labelSelectionSource')"
                                :value="layerSourceType"
                                :options="layerSourceOptions"
                                @selected="$_onSelectLayerSource"
                                :disabled="
                                    isEditMode || conversionToNedGlobeServices
                                "
                                :extendedClass="{
                                    plaintext:
                                        isEditMode ||
                                        conversionToNedGlobeServices
                                }"
                                :required="isOwsNedGlobeServices"
                            >
                            </x-select>
                            <!-- Luchtfoto/database tables -->
                            <b-form-group
                                :label="nedGlobeServicesLabel"
                                v-show="layerSourceType"
                                label-class="font-weight-normal"
                                :label-for="'services-layer-name'"
                            >
                                <b-form-select
                                    :id="'services-layer-id'"
                                    :name="'services-layer-name'"
                                    :value="layerSourceName"
                                    :disabled="isEditMode"
                                    :class="{ plaintext: isEditMode }"
                                    @input="$_onServicesLayerSelected"
                                    :required="
                                        layerSourceType && isOwsNedGlobeServices
                                    "
                                >
                                    <template v-slot:first>
                                        <option :value="null">
                                            {{ $t('selectBoxMakeChoice') }}
                                        </option>
                                    </template>
                                    <template
                                        v-if="
                                            isNedGlobeServicesLayerSourceTable
                                        "
                                    >
                                        <option
                                            v-for="item in tableNames"
                                            :value="item"
                                            :key="item.text"
                                        >
                                            {{ item }}
                                        </option>
                                    </template>
                                    <template
                                        v-if="isNedGlobeServicesLayerSourceLufo"
                                    >
                                        <option
                                            v-for="item in aerialPhotos"
                                            :value="item"
                                            :key="item.text"
                                        >
                                            {{ item }}
                                        </option>
                                    </template>
                                </b-form-select>
                            </b-form-group>
                        </div>
                        <div v-if="selectedOwsServiceLayer">
                            <!-- Graphical Layer -->
                            <x-select
                                v-show="
                                    isOWSLayerWFSSelected &&
                                    !glOwsCombiIsChecked &&
                                    !isOWS_WFS
                                "
                                :id="'ows-graphic-layer-id'"
                                :name="'ows-graphic-layer-name'"
                                :text="$t('labelGraphicLayer')"
                                :value="owsGraphicalLayerValue"
                                :options="comboBoxGraphicalLayers"
                                :simpleMode="true"
                                @selected="$_onGraphicLayerSelected"
                            >
                            </x-select>
                            <b-alert
                                v-show="
                                    $_isStringNotEmpty(
                                        unavailableGraphicalLayerMessage
                                    )
                                "
                                show
                                variant="danger"
                            >
                                {{ unavailableGraphicalLayerMessage }}
                            </b-alert>
                            <!-- RequestTiled -->
                            <x-checkbox
                                v-show="showCheckboxRequestTiled"
                                :id="'request-tiled-id'"
                                :name="'request-tiled-name'"
                                :text="$t('labelRequestTiled')"
                                :value="tiledRequest"
                                @input="$_onRequestTiled"
                                class="mb-3"
                            >
                            </x-checkbox>
                        </div>
                        <!-- Properties -->
                        <theme-layer-properties
                            v-if="
                                selectedOwsServiceLayer ||
                                layerSourceName ||
                                isOGCAPI
                            "
                            ref="themeLayerProperties"
                            :mode="mode"
                            :conversionToNedGlobeServices="
                                conversionToNedGlobeServices
                            "
                            :selectedOwsService="selectedOwsService"
                            :selectedOwsServiceLayer="selectedOwsServiceLayer"
                            :isOwsNedGlobeServices="isOwsNedGlobeServices"
                            :isOWSLayerWFSSelected="isOWSLayerWFSSelected"
                            :isOWSLayerWMTSSelected="isOWSLayerWMTSSelected"
                            :selectionAttributes="selectionAttributes"
                            :imageFormats="imageFormats"
                            :isNedGlobeServicesLayerSourceLufo="
                                isNedGlobeServicesLayerSourceLufo
                            "
                            :isNedGlobeServicesLayerSourceTable="
                                isNedGlobeServicesLayerSourceTable
                            "
                            :isOGCAPI="isOGCAPI"
                            :propertyValues="propertyValues"
                            :stateValues="stateValues"
                            @update="$_onPropertyChanged"
                            @updateState="$_onStateChanged"
                            @checkLayerTitleExists="$_layerTitleExists"
                            @checkLayerNameExists="$_geoServerLayerNameExists"
                            @changeGraphicLayerTitle="$_changeGraphicLayerTitle"
                        >
                        </theme-layer-properties>
                        <!-- Relations -->
                        <template
                            v-if="selectedOwsServiceLayer && canHaveRelations"
                        >
                            <!-- Layer relations -->
                            <themelayer-relations
                                ref="relationsEdit"
                                :id="0"
                                :title="$t('relations.title')"
                                :targetLabel="$t('labelThemelayer')"
                                :relationshipType="
                                    RELATIONSHIP_TYPES.ThemeLayer2ThemeLayer
                                "
                                :layerId="selectedOwsServiceLayer.id"
                                class="pb-3"
                            />
                            <!-- Table relations -->
                            <themelayer-relations
                                v-if="hasNedGlobeServicesOws"
                                ref="relationsTableEdit"
                                :id="1"
                                :title="$t('tableResults.relations')"
                                :targetLabel="$t('tableResults.titleSingular')"
                                :relationshipType="
                                    RELATIONSHIP_TYPES.ThemeLayer2Table
                                "
                                :layerId="selectedOwsServiceLayer.id"
                            >
                                <x-checkbox
                                    v-if="isOwsNedGlobeServices"
                                    name="chkAutoOpen"
                                    :text="$t('relations.autoOpenTabs')"
                                    v-model="autoOpenAllRelationsTabs"
                                    class="mb-3"
                                >
                                </x-checkbox>
                            </themelayer-relations>
                        </template>
                    </div>
                </b-tab>
                <!--Styles-->
                <b-tab :title="$t('labelTabStyle')" v-if="canHaveStyles">
                    <themelayer-styles
                        ref="stylesEdit"
                        :layerName="propertyValues.layerName"
                        :layerId="selectedOwsServiceLayer.id"
                        :informationAttributes="informationAttributes"
                        :geometryTypeArray="geometryTypeArray"
                        :layerSourceName="layerSourceName"
                    />
                </b-tab>
            </b-tabs>
            <!-- Themelayer add / cancel buttons -->
            <submit-cancel-remove
                :is-saving="isSaving"
                :is-deleting="isDeleting"
                :mode="mode"
                @cancel="$_onCancel"
                @remove="$_onRemove"
                :isBtnSaveDisabled="isBtnSaveDisabled"
            >
            </submit-cancel-remove>
        </b-form>
    </b-container>
</template>

<script>
    import { orderBy, debounce } from 'lodash';
    import {
        stringNotEmpty,
        arrayNotEmpty,
        convertToValueTextPair,
        nprogress
    } from '../../services/utilities';
    import {
        MODE_ADD,
        MODE_EDIT,
        OWS_TYPE,
        NEDGLOBE_SERVICES,
        PROJECTION_CODE,
        RELATIONSHIP_TYPES
    } from '../../constants';
    import XSelect from '../../components/BaseComponents/x-select.vue';
    import XCheckbox from '../../components/BaseComponents/x-checkbox.vue';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import SubmitCancelRemove from '../../components/SubmitCancelRemove.vue';
    import ThemeLayerProperties from './ThemeLayerProperties.vue';
    import Relations from '../../components/dashboard/themeLayer/Relations.vue';
    import ThemeLayerStyles from '../../components/dashboard/themeLayer/Styles.vue';

    export default {
        name: 'ThemeLayerDetail',

        mixins: [modalMixin],

        components: {
            'x-select': XSelect,
            'x-checkbox': XCheckbox,
            'themelayer-styles': ThemeLayerStyles,
            'themelayer-relations': Relations,
            'submit-cancel-remove': SubmitCancelRemove,
            'theme-layer-properties': ThemeLayerProperties
        },

        data() {
            return {
                // add/edit mode: $scope.mode
                mode: 0,
                // page title
                title: '',
                RELATIONSHIP_TYPES: RELATIONSHIP_TYPES,

                propertyValues: {
                    isPubliclyAccessible: false,

                    geometryType: '',
                    toleranceType: null,
                    selectionAttribute: null,
                    wmtsStyle: null,

                    tileMatrixSet: null,
                    symbolShiftX: 0.0,
                    symbolShiftY: 0.0,

                    layerCustomTitle: '',
                    layerName: '',

                    description: '',
                    mapTerms: '',
                    keywords: '',
                    metadataUrl: '',
                    infoUrl: '',
                    infoLinkType: '',

                    format: null,
                    baselayer: false,
                    baselayerImage: null,
                    minBaseLayerResolution: null,
                    maxBaseLayerResolution: null
                },

                stateValues: {
                    valideImageState: true,
                    metadataUrlValidState: true,
                    infoUrlValidState: true,
                    titleValidState: true,
                    titleInvalidMessage: '',
                    nameValidState: true,
                    nameInvalidMessage: ''
                },

                // NedGlobe Services
                workspaceName: '',
                layerSourceType: null,
                layerSourceName: null,
                tableNames: [],
                aerialPhotos: [],

                // options
                layerSourceOptions: [
                    {
                        value: 'table',
                        text: this.$t('nedglobeServices.layerSourceTable')
                    },
                    {
                        value: 'lufo',
                        text: this.$t('nedglobeServices.layerSourceLufo')
                    }
                ],

                // tolerance
                toleranceTypes: [
                    {
                        value: '0',
                        text: this.$t('tolerance0mm'),
                        tolerance: 0
                    },
                    {
                        value: '8',
                        text: this.$t('tolerance8mm'),
                        tolerance: 8
                    }
                ],

                layersLoaded: false,
                // themeLayer record object
                editThemeLayer: {},
                // OWS services array: $scope.owsList
                owsServices: [],

                // in 'ows-service-name' geselecteerde object: $scope.ows
                selectedOwsService: null,
                // selected ows services layer: $scope.layer
                selectedOwsServiceLayer: null,
                // graphical layer
                graphicalLayer: null,

                // list with selection attributes
                selectionAttributes: [],

                // list with ímage formats
                imageFormats: [],

                customTitleMaxLength: 50,

                // saving/deleting state (doing)
                isSaving: false,
                isDeleting: false,
                isWaitingForBackend: false,

                // convert to a NedGlobeServes layer
                conversionToNedGlobeServices: false,

                showPopupLayerAdjusted: true,

                // error message for unavailable layer
                owsLayerNameMessage: '',
                validationWarning: null,

                unavailableGraphicalLayerMessage: '',
                glOwsCombiIsChecked: false,
                isOWS_WFS: false,
                autoOpenAllRelationsTabs: false
            };
        },

        computed: {
            isLayerNameRequired() {
                if (this.isOGCAPI) {
                    return false;
                }
                return !this.isOwsNedGlobeServices;
            },
            hasNedGlobeServicesOws() {
                if (this.owsServices && this.owsServices.length > 0) {
                    return (
                        this.owsServices.filter((s) => s.name === NEDGLOBE_SERVICES)
                            .length > 0
                    );
                }
                return false;
            },

            nedGlobeServicesOwsId() {
                if (this.owsServices && this.owsServices.length > 0) {
                    const nedGlobeServicesOws = this.owsServices.filter(
                        (s) => s.name === NEDGLOBE_SERVICES
                    );
                    if (nedGlobeServicesOws && nedGlobeServicesOws.length > 0) {
                        return nedGlobeServicesOws[0].id;
                    }
                }
                return false;
            },

            isTransactionalWfs() {
                return (
                    this.editThemeLayer?.isTransactional ||
                    this.selectedOwsServiceLayer?.isTransactional
                );
            },

            showLayerConvertButton() {
                // Do not show convert button in ADD mode
                if (this.mode === MODE_ADD) {
                    //no convert: Add mode
                    return false;
                }

                // Only show convert button for WFS layers
                if (this.editThemeLayer?.serviceType !== OWS_TYPE.WFS) {
                    //no convert: Not a WFS
                    return false;
                }

                // Only show convert button if organization has NedGlobe Services
                if (!this.hasNedGlobeServicesOws) {
                    //no convert: Has no nedglobe services
                    return false;
                }

                // At startup this code is run when there is not yet a selectedOwsService
                // return false in this situation
                if (!this.selectedOwsService) {
                    //no convert: no selected ows
                    return false;
                }

                // Do not show convert button if this layer is a NedGlobe Services layer
                if (this.selectedOwsService?.name === NEDGLOBE_SERVICES) {
                    //no convert: this is nedglobeservices
                    return false;
                }

                // Do not show button is there is no uri_wfs
                if (!this.selectedOwsService?.uri_WFS) {
                    //no convert: No uri WFS
                    return false;
                }

                // Do not show button if it is a WFS-T layer and the user is not a superuser
                if (
                    this.isTransactionalWfs &&
                    !this.$store.getters.userInfo.isSuper
                ) {
                    return false;
                }

                // Do not show convert button if layer comes from any of these sources:
                // PDOK
                // Nationaal georegister
                if (
                    this.selectedOwsService?.uri_WFS
                        ?.toUpperCase()
                        ?.includes('SERVICE.PDOK.NL')
                ) {
                    //no convert: PDOK
                    return false;
                }

                // Final condition return in one statement to satisfy sonarcloud although it makes the code more complex.
                // Return true if URL is not nationaalgeoregister.
                return !this.selectedOwsService?.uri_WFS
                    ?.toUpperCase()
                    ?.includes('GEODATA.NATIONAALGEOREGISTER.NL');
            },

            informationAttributes() {
                return this.selectionAttributes.map((item) => ({
                    attribute: item.value,
                    alias: item.value,
                    dataType: item.dataType
                }));
            },

            isOwsNedGlobeServices() {
                return this.selectedOwsService?.name === NEDGLOBE_SERVICES;
            },

            isNedGlobeServicesLayerSourceLufo() {
                return this.layerSourceType === 'lufo';
            },

            isNedGlobeServicesLayerSourceTable() {
                return this.layerSourceType === 'table';
            },

            nedGlobeServicesLabel() {
                return this.isNedGlobeServicesLayerSourceTable
                    ? this.$t('nedglobeServices.tableName')
                    : this.$t('nedglobeServices.layerSourceLufo');
            },

            geometryTypeArray() {
                return this.propertyValues.geometryType
                    ? this.propertyValues.geometryType.split('')
                    : [];
            },

            /**
             * Check if themelayer can be saved
             * @returns {boolean} indicating if themelayer can be saved
             */
            isBtnSaveDisabled() {
                return (
                    !this.stateValues.titleValidState ||
                    !this.stateValues.nameValidState ||
                    this.isSaving ||
                    this.isDeleting ||
                    this.isWaitingForBackend ||
                    (this.propertyValues.baselayer &&
                        this.propertyValues.baselayerImage === null)
                );
            },

            owsServicesId() {
                if (this.selectedOwsServiceLayer) {
                    return `${this.selectedOwsServiceLayer.sourceId}`;
                } else if (this.selectedOwsService) {
                    return `${this.selectedOwsService.id}`;
                }
                return null;
            },

            /**
             * Selected layer name
             * @return {string} current selected layername or an empty string
             */
            owsLayerValue() {
                if (this.selectedOwsServiceLayer) {
                    return `${this.selectedOwsServiceLayer.serviceType}:${this.selectedOwsServiceLayer.layerName}`;
                }
                return null;
            },

            canHaveRelations() {
                return (
                    this.isEditMode &&
                    this.selectedOwsServiceLayer?.serviceType === OWS_TYPE.WFS
                );
            },

            canHaveStyles() {
                return (
                    this.isOwsNedGlobeServices &&
                    this.isEditMode &&
                    this.selectedOwsServiceLayer &&
                    this.isNedGlobeServicesLayerSourceTable
                );
            },

            owsGraphicalLayerValue() {
                // Fill automatically the graphical layer name when creating a new theme layer or
                // when the OWS layer of existing theme layer is not available anymore
                if (
                    (!this.isEditMode && this.graphicalLayer) ||
                    (this.isEditMode &&
                        this.graphicalLayer &&
                        this.editThemeLayer.unavailable)
                ) {
                    return this.graphicalLayer.layerName;
                    // Otherwise return the defined graphical layer name
                } else if (this.editThemeLayer) {
                    // Validate the availability of graphical layer
                    this.$_validateGraphicalLayerAvailability();
                    return this.editThemeLayer.graphicalLayerName;
                }
                return null;
            },

            editThemeLayerName() {
                return this.isOwsNedGlobeServices
                    ? this.editThemeLayer.geoServerLayerName
                    : this.editThemeLayer.layerName;
            },

            /***************************************************************************************************************
             **** enable/disable
             ****************************************************************************************************************/
            disableLayerName() {
                if (!this.isEditMode) {
                    return false;
                }
                if (this.editThemeLayer?.unavailable) {
                    return false;
                }
                if (
                    !this.selectedOwsService ||
                    this.selectedOwsService.unavailable
                ) {
                    return false;
                }
                return !!this.selectedOwsServiceLayer;
            },

            /**
             * In edit mode yes or no?
             * @return {bool} true if in edit mode, false otherwise
             */
            isEditMode() {
                return (
                    this.mode === MODE_EDIT && !this.conversionToNedGlobeServices
                );
            },

            tiledRequest() {
                if (this.selectedOwsServiceLayer) {
                    return this.selectedOwsServiceLayer.tiledRequest;
                }
                return false;
            },

            /***************************************************************************************************************
             **** v-show properties
             ****************************************************************************************************************/

            /**
             * Is WFS layer selected?
             * @return {Boolean} [true if visible, false otherwise]
             */
            isOWSLayerWFSSelected() {
                return (
                    this.selectedOwsServiceLayer &&
                    this.selectedOwsServiceLayer.serviceType === OWS_TYPE.WFS
                );
            },

            /**
             * Is WMTS layer selected?
             * @return {Boolean} [true if visible, false otherwise]
             */
            isOWSLayerWMTSSelected() {
                return (
                    this.selectedOwsServiceLayer &&
                    this.selectedOwsServiceLayer.serviceType === OWS_TYPE.WMTS
                );
            },

            /**
             * Show checkbox request tiled?
             * Show if layer is WMS or
             * Show if layer is WFS and graphical layer
             * Do not show for NedGlobe Services
             * @return {Boolean} [true if visible, false otherwise]
             */
            showCheckboxRequestTiled() {
                if (
                    !this.isOwsNedGlobeServices &&
                    this.selectedOwsServiceLayer &&
                    this.selectedOwsServiceLayer.serviceType === OWS_TYPE.WMS
                ) {
                    return true;
                }
                return (
                    !this.isOwsNedGlobeServices &&
                    this.selectedOwsServiceLayer &&
                    this.selectedOwsServiceLayer.serviceType === OWS_TYPE.WFS &&
                    this.graphicalLayer?.layerName
                );
            },

            /***************************************************************************************************************
             **** combobox arrays
             ****************************************************************************************************************/

            /**
             * Get OWS services for 'OWS service' combobox
             * @return {array} array with OWS services objects
             */
            comboBoxOwsServices() {
                return this.owsServices
                    .filter((item) => !item.unavailable)
                    .map((item) => {
                        return {
                            id: item.id,
                            unavailable: item.unavailable,
                            // fields for x-select (b-form-select)
                            value: `${item.id}`,
                            text: item.name
                        };
                    });
            },

            /**
             * Get service layers for layers combobox
             * @return {array} array with layers
             */
            comboBoxOwsServiceWfsLayers() {
                return this.$_getLayerOptions(OWS_TYPE.WFS);
            },

            /**
             * Get service layers for layers combobox
             * @return {array} array with layers
             */
            comboBoxOwsServiceWmsLayers() {
                return this.$_getLayerOptions(OWS_TYPE.WMS);
            },

            /**
             * Get service layers for layers combobox
             * @return {array} array with layers
             */
            comboBoxOwsServiceWmtsLayers() {
                return this.$_getLayerOptions(OWS_TYPE.WMTS);
            },

            /**
             * Combobox data for graphical layers
             * @return {[type]} [description]
             */
            comboBoxGraphicalLayers() {
                if (this.selectedOwsService?.layers) {
                    // filter serviceType not equal WFS
                    let filteredLayers = this.selectedOwsService.layers.filter(
                        (item) =>
                            item.serviceType === OWS_TYPE.WMS &&
                            item.layerName !== null
                    );
                    filteredLayers = orderBy(filteredLayers, [
                        (o) => o.title.toLowerCase()
                    ]);
                    return convertToValueTextPair(
                        filteredLayers,
                        'layerName',
                        'title'
                    );
                }
                return [];
            },
            isOGCAPI() {
                return !!this.selectedOwsService?.uri_OGC;
            }
        },

        mounted() {
            // load OWS services
            this.$_loadOwsList().then(
                async () => {
                    // load themelayer
                    if (this.$route.params.id && this.$route.params.id > 0) {
                        this.mode = MODE_EDIT;
                        this.title = this.$t('themelayerDetailEditTitle');
                        // get existing themelayer data from the backend
                        this.$store
                            .dispatch('useApiData', {
                                name: 'GetThemeLayerDetails',
                                parameters: {
                                    id: this.$route.params.id
                                }
                            })
                            .then(
                                async (result) => {
                                    this.editThemeLayer = { ...result.data };
                                    // select ows service
                                    this.selectedOwsService = this.owsServices.find(
                                        (item) => {
                                            return (
                                                item.id ===
                                                this.editThemeLayer.sourceId
                                            );
                                        }
                                    );

                                    // show table columns that are restricted GeoServer names
                                    await this.$_showGeoServerRestrictedNames(
                                        this.editThemeLayer
                                    );
                                },
                                (error) => {
                                    this.showToast({
                                        title: this.$t('layerGetFailed'),
                                        message: `${this.$t(
                                            'layerGetError'
                                        )} ${this.$t('errorWas')} ${
                                            error.message
                                        }.`,
                                        variant: 'danger',
                                        autoHideDelay: 5000
                                    });
                                }
                            );
                    } else {
                        // new form
                        this.mode = MODE_ADD;
                        this.title = this.$t('themelayerDetailAddTitle');
                    }
                },
                (error) => {
                    this.showToast({
                        title: this.$t('owsServicesGetFailed'),
                        message: `${this.$t('owsGetFailedError')} ${this.$t(
                            'errorWas'
                        )} ${error.message}.`,
                        variant: 'danger',
                        autoHideDelay: 5000
                    });
                }
            );
        },

        created() {
            // preload SVG file list for use in SVG based styles
            this.$_setAvailableSvgFiles();
        },

        methods: {
            async $_onConversionToNedGlobeServices() {
                this.conversionToNedGlobeServices = true;
                await this.$_onOwsSelected(this.nedGlobeServicesOwsId);
                await this.$_loadServicesLayers();
                this.layerSourceType = 'table';
                this.layerSourceName = null;
            },

            $_onPropertyChanged(value) {
                this.propertyValues = value;
            },

            $_onStateChanged(value) {
                this.stateValues = value;
            },

            $_isStringNotEmpty(str) {
                return stringNotEmpty(str);
            },

            $_onSelectLayerSource(value) {
                this.layerSourceType = value;
                if (this.mode === MODE_ADD) {
                    this.layerSourceName = null;
                }
            },
            async $_setAvailableSvgFiles() {
                await this.$store.dispatch('setAvailableSvgFiles');
            },

            async $_getWorkspaceName() {
                if (this.isOwsNedGlobeServices && this.workspaceName === '') {
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'GetWorkspaceName'
                    });
                    this.workspaceName = result.data ? `${result.data}:` : '';
                }
            },

            async $_showGeoServerRestrictedNames(themeLayer) {
                if (
                    !themeLayer.layerSourceType ||
                    themeLayer.layerSourceType !== 'table'
                ) {
                    return;
                }

                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetRestrictedColumnNames',
                    parameters: { LayerName: themeLayer.layerName }
                });

                if (result.data && result.data.length > 0) {
                    const message = this.$t(
                        'nedglobeServices.tables.tableRestrictedNames',
                        [result.data.join(', ')]
                    );

                    this.showToast({
                        title: this.$t(
                            'nedglobeServices.tables.tableRestrictedNamesWarningTitle',
                            [themeLayer.layerSourceName]
                        ),
                        message: message,
                        variant: 'warning',
                        noAutoHide: true
                    });
                }
            },

            async $_onServicesLayerSelected(value) {
                this.layerSourceName = value;
                this.stateValues.nameValidState = true;

                if (value && !this.isEditMode) {
                    try {
                        this.isWaitingForBackend = true;

                        if (this.isNedGlobeServicesLayerSourceLufo) {
                            const result = await this.$store.dispatch(
                                'useApiData',
                                {
                                    name: 'GetAerialPhotoImageFormats',
                                    parameters: { AerialPhotoName: value }
                                }
                            );
                            this.imageFormats = result.data;
                        } else {
                            const result = await this.$store.dispatch(
                                'useApiData',
                                {
                                    name: 'GetTableAttributes',
                                    parameters: { TableName: value }
                                }
                            );
                            this.selectionAttributes = convertToValueTextPair(
                                result.data,
                                'name',
                                'name'
                            );
                            await this.$_setGeometryType();
                        }
                    } finally {
                        this.isWaitingForBackend = false;
                    }
                }
            },

            async $_loadServicesLayers() {
                if (this.tableNames.length === 0) {
                    const tableNamesResult = await this.$store.dispatch(
                        'useApiData',
                        {
                            name: 'GetTableNames',
                            parameters: { geometrytables: true }
                        }
                    );
                    this.tableNames = tableNamesResult.data;
                }
                if (this.aerialPhotos.length === 0) {
                    const aerialPhotosResult = await this.$store.dispatch(
                        'useApiData',
                        { name: 'GetAerialPhotos' }
                    );
                    this.aerialPhotos = aerialPhotosResult.data;
                }
            },

            // Checks whether the used layer in this theme is available
            $_checkOwsLayerNameMessage() {
                this.owsLayerNameMessage = '';
                if (
                    this.layersLoaded &&
                    this.selectedOwsService &&
                    this.editThemeLayer.layerName
                ) {
                    const layerName = this.isOwsNedGlobeServices
                        ? this.editThemeLayer.geoServerLayerName
                        : this.editThemeLayer.layerName;
                    let availableLayer;
                    if (
                        this.selectedOwsService.layers &&
                        this.selectedOwsService.layers.length > 0
                    ) {
                        availableLayer = this.selectedOwsService.layers.find(
                            (layer) => layer.layerName === layerName
                        );
                    }
                    if (!availableLayer) {
                        this.owsLayerNameMessage = `De huidige laagnaam: "${layerName}" bestaat niet meer in deze OWS. Kies een bestaande uit de lijst.`;
                        // OWS layer is not available, so reset the graphical layer belonging to this layer
                        this.editThemeLayer.graphicalLayerName = null;
                    }
                }
            },

            // Set customTitle with a maximum of 50 characters
            $_setLimitedCustomTitleLength(newTitle) {
                if (newTitle) {
                    this.propertyValues.layerCustomTitle = newTitle.substring(
                        0,
                        this.customTitleMaxLength
                    );
                }
            },

            // Set customName with a maximum of 50 characters
            $_setLimitedCustomLayerNameLength(newName) {
                this.propertyValues.layerName =
                    newName && newName.substring(0, this.customTitleMaxLength);
            },

            /***************************************************************************************************************
             **** OWS services
             ****************************************************************************************************************/
            // Load OWS services from the API
            $_loadOwsList() {
                return new Promise((resolve, reject) => {
                    this.$store
                        .dispatch('useApiData', {
                            name: 'GetOwsOverview'
                        })
                        .then(
                            (result) => {
                                if (arrayNotEmpty(result.data)) {
                                    this.owsServices = result.data;
                                }
                                resolve(true);
                            },
                            (error) => {
                                this.showToast({
                                    title: this.$t('owsServicesGetFailed'),
                                    message: `${this.$t(
                                        'owsGetFailedError'
                                    )} ${this.$t('errorWas')} ${error.message}.`,
                                    variant: 'danger',
                                    autoHideDelay: 5000
                                });
                                resolve(false);
                            }
                        );
                });
            },

            /**
             * OWS selected event handler
             * @param  {[type]} value [description]
             */
            async $_onOwsSelected(value) {
                this.stateValues.nameValidState = true;
                this.stateValues.nameInvalidMessage = null;
                this.stateValues.titleValidState = true;
                this.stateValues.titleInvalidMessage = null;

                this.layerSourceType = null;
                this.layerSourceName = null;
                this.isOWS_WFS = false;

                if (!value) {
                    this.selectedOwsService = null;
                    this.selectedOwsServiceLayer = null;
                    return;
                }

                const sourceId = parseInt(value);
                const records = this.owsServices.filter(
                    (item) => item.id === sourceId
                );
                if (arrayNotEmpty(records)) {
                    this.selectedOwsService = records[0];
                }
                // Do not load layers if no OWS selected, or ows unavailable
                if (!this.selectedOwsService) {
                    return;
                }

                // get workspace name for NedGlobe Services
                await this.$_getWorkspaceName();
                // load layers
                this.$_loadLayers().then((result) => {
                    this.layersLoaded = true;
                    this.$_checkOwsLayerNameMessage();
                });

                if (!this.isOwsNedGlobeServices) {
                    return;
                }

                await this.$_loadServicesLayers();
                this.layersLoaded = true;

                if (this.mode === MODE_ADD) {
                    this.selectionAttributes = [];

                    this.propertyValues.layerCustomTitle = null;
                    this.propertyValues.layerName = null;
                    this.selectedOwsServiceLayer = null;
                }

                // layername contains of the following (3) parts:
                //      <workspace>:<organisatie_code>_<layername>
                // or (2) parts:
                //      <workspace>:<layername>
                if (this.propertyValues.layerName) {
                    // strip GeoServer workspace from layername: topp:O10002_aed => O10002_aed
                    let tokens = this.propertyValues.layerName.split(':');
                    if (tokens.length > 1) {
                        // layername = <organisatie_code>_<layername> or <layername>
                        this.propertyValues.layerName = `${tokens[1]}`;

                        tokens = this.propertyValues.layerName.split('_');
                        // if tokens length = 1 then layername was <layername>
                        // if tokens length > 1 then layername was <organisatie_code>_<layername>
                        if (tokens.length > 1) {
                            // layername = <layername>
                            this.propertyValues.layerName = `${tokens
                                .slice(1)
                                .join('_')}`;
                        }
                    }
                }
            },

            /***************************************************************************************************************
             **** OWS services layer
             ****************************************************************************************************************/

            /**
             * get an item from the owsServiceLayers array
             * @param  {string} layerName   [name of the layer]
             * @param  {string} serviceType [name of the servicetype]
             * @return {*}                  [selected array element or null]
             */
            $_getOwsServiceLayer(layerName, serviceType) {
                const filteredLayers = this.selectedOwsService.layers.filter(
                    (item) => {
                        return (
                            item.layerName === layerName &&
                            item.serviceType === serviceType
                        );
                    }
                );
                if (arrayNotEmpty(filteredLayers)) {
                    return filteredLayers[0];
                }
                return null;
            },

            /**
             * Set the properties of the layer from the saved properties of the edited layer
             */
            $_setProperties() {
                this.graphicalLayer = this.$_getOwsServiceLayer(
                    this.editThemeLayer.graphicalLayerName,
                    OWS_TYPE.WMS
                );
                if (!this.graphicalLayer) {
                    // create new object
                    this.graphicalLayer = {};
                    this.graphicalLayer.layerName =
                        this.editThemeLayer.graphicalLayerName;
                    if (this.graphicalLayer.layerName) {
                        this.showGraphlayerField = true;
                    }
                } else {
                    this.graphicalLayer.graphicalLayerName =
                        this.editThemeLayer.graphicalLayerName;
                }
                this.layerSourceType = this.editThemeLayer.layerSourceType;
                this.layerSourceName = this.editThemeLayer.layerSourceName;
                this.autoOpenAllRelationsTabs =
                    this.editThemeLayer.autoOpenAllRelationsTabs;

                this.propertyValues.isPubliclyAccessible =
                    this.editThemeLayer.isPubliclyAccessible;

                this.propertyValues.geometryType = this.editThemeLayer.geometryType;

                if (this.selectedOwsServiceLayer) {
                    this.selectedOwsServiceLayer.tolerance =
                        this.editThemeLayer.tolerance;
                    this.propertyValues.toleranceType =
                        this.selectedOwsServiceLayer.tolerance === 0
                            ? this.toleranceTypes[0]
                            : this.toleranceTypes[1];
                }

                this.propertyValues.selectionAttribute =
                    this.editThemeLayer.selectionAttribute;
                this.propertyValues.wmtsStyle = this.editThemeLayer.wmtsStyle;

                this.propertyValues.tileMatrixSet =
                    this.editThemeLayer.tileMatrixSet;
                this.propertyValues.symbolShiftX = this.editThemeLayer.symbolShiftX;
                this.propertyValues.symbolShiftY = this.editThemeLayer.symbolShiftY;

                this.propertyValues.description = this.editThemeLayer.description;
                this.propertyValues.mapTerms = this.editThemeLayer.mapTerms;
                this.propertyValues.keywords = this.editThemeLayer.keywords;
                this.propertyValues.metadataUrl = this.editThemeLayer.metadataUrl;
                this.propertyValues.infoUrl = this.editThemeLayer.infoUrl;
                this.propertyValues.infoLinkType = this.editThemeLayer.infoLinkType;

                this.propertyValues.format = this.editThemeLayer.format;
                this.propertyValues.baselayer = this.editThemeLayer.baselayer;
                this.propertyValues.minBaseLayerResolution =
                    this.editThemeLayer.minBaseLayerResolution;
                this.propertyValues.maxBaseLayerResolution =
                    this.editThemeLayer.maxBaseLayerResolution;
                this.propertyValues.baselayerImage =
                    this.editThemeLayer.baselayerImage;

                // title
                this.$_setLimitedCustomTitleLength(this.editThemeLayer.title);

                // layerName
                if (this.isOwsNedGlobeServices && this.editThemeLayer.layerName) {
                    this.editThemeLayer.layerName =
                        this.editThemeLayer.layerName.replace(
                            this.workspaceName,
                            ''
                        );
                }
                this.$_setLimitedCustomLayerNameLength(
                    this.editThemeLayer.layerName
                );
            },

            /**
             * Load layers from the API
             * @return {*} promise object
             */
            $_loadLayers() {
                return new Promise((resolve, reject) => {
                    nprogress.start();
                    // get OWS layers from the API
                    this.$store
                        .dispatch('useApiData', {
                            name: 'GetIncludeLayers',
                            parameters: {
                                id: this.selectedOwsService.id
                            }
                        })
                        .then(
                            (result) => {
                                // Continue if there are layers present or if this is NedGlobe Sercvies OWS (then layers will be added).
                                if (
                                    result.data &&
                                    (arrayNotEmpty(result.data.layers) ||
                                        this.isOwsNedGlobeServices ||
                                        this.isOGCAPI)
                                ) {
                                    // replace selected OWS with OWS + layers
                                    this.selectedOwsService = result.data;
                                    // post processing mode edit
                                    if (this.editThemeLayer) {
                                        const layerName = this.editThemeLayerName;
                                        this.selectedOwsServiceLayer =
                                            this.$_getOwsServiceLayer(
                                                layerName,
                                                this.editThemeLayer.serviceType
                                            );

                                        if (this.selectedOwsServiceLayer) {
                                            this.$_bindProperties();
                                        }
                                        // Execute setProperties when we have a OWS layer or a OGC API layer.
                                        if (
                                            this.selectedOwsServiceLayer ||
                                            this.isOGCAPI
                                        ) {
                                            this.$_setProperties();
                                        }
                                    }
                                } else {
                                    this.msgBoxOk({
                                        message: this.$t('layersNotfound')
                                    });
                                }
                                // New OWS is chosen, reset error message if there is any
                                this.unavailableGraphicalLayerMessage = '';
                                nprogress.done();
                                resolve(true);
                            },
                            (error) => {
                                const errorMsg = this.$t(
                                    'owsGetServiceFailedError',
                                    [`${this.selectedOwsService.name}`]
                                );
                                this.showToast({
                                    title: this.$t('owsServiceGetFailed'),
                                    message: `${errorMsg} ${this.$t('errorWas')} ${
                                        error.message
                                    }.`,
                                    variant: 'danger',
                                    autoHideDelay: 5000
                                });
                                nprogress.done();
                                resolve(false);
                            }
                        );
                });
            },

            /**
             *  Bind properties of editLayer to display layer
             */
            $_bindProperties() {
                if (this.mode === MODE_EDIT) {
                    if (this.selectedOwsServiceLayer) {
                        this.selectedOwsServiceLayer.id = this.editThemeLayer.id;
                        this.selectedOwsServiceLayer.format =
                            this.editThemeLayer.format;
                        this.selectedOwsServiceLayer.description =
                            this.editThemeLayer.description;
                        this.selectedOwsServiceLayer.mapTerms =
                            this.editThemeLayer.mapTerms;
                        this.selectedOwsServiceLayer.keywords =
                            this.editThemeLayer.keywords;
                        this.selectedOwsServiceLayer.baselayer =
                            this.editThemeLayer.baselayer;
                        this.selectedOwsServiceLayer.tileMatrixSet =
                            this.editThemeLayer.tileMatrixSet;
                        this.selectedOwsServiceLayer.baselayerImage =
                            this.editThemeLayer.baselayerImage;
                        this.selectedOwsServiceLayer.wmtsStyle =
                            this.editThemeLayer.wmtsStyle;
                        this.selectedOwsServiceLayer.tiledRequest =
                            this.editThemeLayer.tiledRequest;
                        this.selectedOwsServiceLayer.metadataUrl =
                            this.editThemeLayer.metadataUrl;
                        this.selectedOwsServiceLayer.infoUrl =
                            this.editThemeLayer.infoUrl;
                        this.selectedOwsServiceLayer.infoLinkType =
                            this.editThemeLayer.infoLinkType;
                        this.selectedOwsServiceLayer.geometryType =
                            this.editThemeLayer.geometryType;
                        this.selectedOwsServiceLayer.isPubliclyAccessible =
                            this.editThemeLayer.isPubliclyAccessible;
                        this.selectedOwsServiceLayer.isTransactional =
                            this.editThemeLayer.isTransactional;
                        if (!this.selectedOwsServiceLayer.layerName) {
                            this.selectedOwsServiceLayer.layerName =
                                this.editThemeLayer.layerName;
                        }
                    }
                } else {
                    this.$_setCustomTitle();
                    this.$_setCustomLayerName();
                    this.$_setToleranceType();
                    this.$_setGeometryType();
                }
            },

            /**
             * Combobox Layername can contain non unique values
             * Therefore ServiceType is added to the value to make it unique
             * This function returns the original value and ServiceType
             * @param  {string} value Selected layerName
             * @return {bool} object with value and ServiceType
             */
            $_getValueAndServiceTypeFromOption(value) {
                if (!value) {
                    return null;
                }
                if (value.startsWith('WFS:')) {
                    return {
                        serviceType: OWS_TYPE.WFS,
                        value: value.replace('WFS:', '')
                    };
                }
                if (value.startsWith('WMS:')) {
                    return {
                        serviceType: OWS_TYPE.WMS,
                        value: value.replace('WMS:', '')
                    };
                }
                if (value.startsWith('WMTS:')) {
                    return {
                        serviceType: OWS_TYPE.WMTS,
                        value: value.replace('WMTS:', '')
                    };
                }
                return null;
            },

            /**
             * Combobox <!-- Layername --> selected event handler
             * @param  {string} value Selected layerName
             */
            $_onLayerSelected(value) {
                // Reset the error message if there was any, because the selected layer is chosen
                // from a list of available layers (unavailable === false)
                this.owsLayerNameMessage = '';
                const newValueObject =
                    this.$_getValueAndServiceTypeFromOption(value);
                this.selectedOwsServiceLayer = null;

                if (!newValueObject) {
                    return;
                }

                const filtered = this.selectedOwsService.layers.filter(
                    (item) =>
                        item.layerName === newValueObject.value &&
                        item.serviceType === newValueObject.serviceType
                );
                if (!arrayNotEmpty(filtered)) {
                    return;
                }

                this.selectedOwsServiceLayer = filtered[0];
                if (!this.selectedOwsServiceLayer) {
                    return;
                }

                this.$_bindProperties();

                if (
                    this.selectedOwsService.readonly &&
                    !this.isEditMode &&
                    !this.isOGCAPI
                ) {
                    // for NedGlobe Services (readonly) layers the name must be unique
                    this.$_layerNameExists(newValueObject);
                } else {
                    this.stateValues.nameValidState = true;
                    this.stateValues.nameInvalidMessage = null;
                    // for other layers the title must be unique (duplicate names allowed)
                    this.$_layerTitleExists(this.propertyValues.layerCustomTitle);
                }
                // Set default matrixset & graphicalLayer only when layer is known
                if (this.selectedOwsServiceLayer.layerName) {
                    this.$_setDefaultMatrixSet();
                    // set initial values
                    this.propertyValues.description =
                        this.selectedOwsServiceLayer.description;
                    this.propertyValues.mapTerms =
                        this.selectedOwsServiceLayer.mapTerms;
                    this.propertyValues.keywords =
                        this.selectedOwsServiceLayer.keywords;
                    this.propertyValues.metadataUrl =
                        this.selectedOwsServiceLayer.metadataUrl;
                    this.propertyValues.infoUrl =
                        this.selectedOwsServiceLayer.infoUrl;
                    this.propertyValues.infoLinkType =
                        this.selectedOwsServiceLayer.infoLinkType;
                    this.propertyValues.tileMatrixSet =
                        this.selectedOwsServiceLayer.tileMatrixSet;

                    // Process selected WFS layer
                    if (
                        this.selectedOwsServiceLayer &&
                        this.selectedOwsServiceLayer.serviceType === OWS_TYPE.WFS
                    ) {
                        // In case of WFS layer, try to set equally named WMS layer as graphical layer
                        this.$_setEqualGraphicalLayerName();
                        this.$_getFeatureTypeInfo();
                    }
                }
            },

            // Get DescribeFeatureType info for the selected layer
            async $_getFeatureTypeInfo() {
                nprogress.start();
                try {
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'GetFeatureTypeInfoByName',
                        parameters: {
                            layerName: this.selectedOwsServiceLayer.layerName,
                            owsId: this.selectedOwsService.id
                        }
                    });
                    // Set geometry column for the selected layer
                    this.selectedOwsServiceLayer.geometryColumn =
                        result.data.geometryColumn;
                    // set selection attributes
                    this.selectionAttributes = result.data.attributes
                        .filter((attr) => !attr.isGeometry && attr.name !== 'id')
                        .sort((attr1, attr2) => {
                            if (attr1.name < attr2.name) {
                                return -1;
                            }
                            if (attr1.name > attr2.name) {
                                return 1;
                            }
                            return 0;
                        })
                        .map((attr) => ({
                            value: attr.name,
                            text: attr.name,
                            dataType: attr.type.replace('xsd:', '')
                        }));
                    nprogress.done();
                } catch (error) {
                    nprogress.done();
                }
            },

            /***************************************************************************************************************
             **** Graphical layer
             ****************************************************************************************************************/

            /**
             *  Checks if there is a graphicalLayerName (WMS) with equal name to the chosen WFS layer name
             *  If no exact match found, use string after : as searchterm
             *  Sets the matched layer as graphical layer name
             */
            $_setEqualGraphicalLayerName() {
                // Try exact match
                let layerName = this.selectedOwsServiceLayer.layerName;
                this.graphicalLayer = this.$_getGraphicalLayer(layerName);
                if (!this.graphicalLayer) {
                    // Look for a match based on title
                    this.graphicalLayer = this.$_matchGraphicalLayerByTitle(
                        this.selectedOwsServiceLayer.title
                    );
                } else if (this.isEditMode) {
                    this.graphicalLayer.graphicalLayerName =
                        this.editThemeLayer.graphicalLayerName;
                } else {
                    this.graphicalLayer.graphicalLayerName =
                        this.graphicalLayer.layerName;
                }
            },

            /**
             *  get the graphical layer
             *  @param {*} [layerName] [layer name to search for]
             */
            $_getGraphicalLayer(layerName) {
                // First, check if layer is selected (and not combo option 'Select')
                if (!layerName) {
                    return null;
                }

                // match WFS and WMS layer names as is
                let matchedLayer = this.$_matchGraphicalLayer(layerName);

                // Ook hieronder lijkt me overbodig aangezien de naam verderop ook al geplitst wordt.
                // no match found, split WFS layer name
                if (!matchedLayer) {
                    // split layername in tokens (1..n)
                    const layerTokens = layerName.split(':');
                    // last token contains layername
                    const partialLayerName = layerTokens[layerTokens.length - 1];
                    // match WFS and WMS layer name
                    matchedLayer = this.$_matchGraphicalLayer(partialLayerName);
                }
                return matchedLayer;
            },

            /**
             *  get the graphical layer
             *  @param {*} [layerName] [layer name to search for]
             */
            $_matchGraphicalLayer(layerName) {
                let matchedLayer = null;

                if (this.selectedOwsService?.layers) {
                    // get list of WMS layers with an equal name to the WFS layer
                    const filtered = this.selectedOwsService.layers.filter(
                        (item) => {
                            // match WMS layer case insensitive
                            return (
                                item.serviceType === OWS_TYPE.WMS &&
                                this.$_layernamesAreEqual(layerName, item.layerName)
                            );
                        }
                    );
                    if (arrayNotEmpty(filtered)) {
                        matchedLayer = filtered[0];
                    }
                }
                return matchedLayer;
            },

            /**
             *  get the graphical layer
             *  @param {*} [layerName] [layer name to search for]
             */
            $_matchGraphicalLayerByTitle(layerTitle) {
                let matchedLayer = null;

                if (this.selectedOwsService?.layers) {
                    // get list of WMS layers with an equal name to the WFS layer
                    const filtered = this.selectedOwsService.layers.filter(
                        (item) => {
                            // match WMS layer case insensitive
                            return (
                                item.serviceType === OWS_TYPE.WMS &&
                                this.$_layernamesAreEqual(layerTitle, item.title)
                            );
                        }
                    );
                    if (arrayNotEmpty(filtered)) {
                        matchedLayer = filtered[0];
                    }
                }
                return matchedLayer;
            },

            /**
             * Graphical layername selected in the select
             * @param  {string} value [selected value]
             */
            $_onGraphicLayerSelected(value) {
                this.graphicalLayer = this.$_getGraphicalLayer(value);
                if (this.graphicalLayer) {
                    // Set the graphicalLayerName property to the layerName
                    this.graphicalLayer.graphicalLayerName =
                        this.graphicalLayer.layerName;
                    this.editThemeLayer.graphicalLayerName =
                        this.graphicalLayer.layerName;
                } else {
                    this.editThemeLayer.graphicalLayerName = value;
                }
                // New selection is made, reset the error message
                this.unavailableGraphicalLayerMessage = '';
            },

            /***************************************************************************************************************
             **** Input fields
             ****************************************************************************************************************/

            /**
             * Request tiled option changed
             */
            $_onRequestTiled(value) {
                this.selectedOwsServiceLayer.tiledRequest = value;
            },

            async $_setToleranceType() {
                if (
                    this.isOwsNedGlobeServices &&
                    this.isNedGlobeServicesLayerSourceTable &&
                    this.layerSourceName
                ) {
                    const parameters = { TableName: this.layerSourceName };
                    const postGisLayerTolerance = await this.$store.dispatch(
                        'useApiData',
                        {
                            name: 'GetPostGisLayerTolerance',
                            parameters
                        }
                    );
                    this.propertyValues.toleranceType =
                        postGisLayerTolerance.data === 0
                            ? this.toleranceTypes[0]
                            : this.toleranceTypes[1];
                }
            },

            async $_setGeometryType() {
                if (
                    this.isOwsNedGlobeServices &&
                    this.isNedGlobeServicesLayerSourceTable &&
                    this.layerSourceName
                ) {
                    const parameters = { TableName: this.layerSourceName };
                    const postGisLayerGeometryType = await this.$store.dispatch(
                        'useApiData',
                        {
                            name: 'GetPostGisLayerGeometryTypes',
                            parameters
                        }
                    );
                    // API returns array of geometry types, convert to string
                    let geometryType = postGisLayerGeometryType.data.join('');
                    this.propertyValues.geometryType = geometryType;
                }
            },

            $_setCustomLayerName() {
                if (this.isOwsNedGlobeServices) {
                    const capitalizedTitle =
                        this.layerSourceName.charAt(0).toUpperCase() +
                        this.layerSourceName.slice(1);
                    this.$_setLimitedCustomLayerNameLength(capitalizedTitle);
                    // check name exists
                    this.$_geoServerLayerNameExists(this.propertyValues.layerName);
                }
            },

            /**
             *  Layer title copy from source.
             */
            $_setCustomTitle() {
                const layerTitle = this.selectedOwsServiceLayer
                    ? this.selectedOwsServiceLayer.title
                    : this.layerSourceName;
                const capitalizedTitle =
                    layerTitle.charAt(0).toUpperCase() + layerTitle.slice(1);
                this.$_setLimitedCustomTitleLength(
                    capitalizedTitle.replace(/_/g, ' ')
                );
                // check title exists
                this.$_layerTitleExists(this.propertyValues.layerCustomTitle);
            },

            async $_removeLayerFromCache(layerId) {
                try {
                    const parameters = { id: layerId };
                    await this.$store.dispatch('useApiData', {
                        name: 'DeleteLayerFromCache',
                        parameters
                    });
                } catch (error) {
                    this.showToast({
                        title: this.$t('removeFromLayerCacheErrorTitle'),
                        message: this.$t('removeFromLayerCacheErrorMessage'),
                        variant: 'warning',
                        autoHideDelay: 10000
                    });
                }
            },

            /**
             * Checks if the given themelayer name already exists in the database
             * @return {void} - Sets error message if needed
             */
            $_layerTitleExists: debounce(async function (searchTerm) {
                const parameters = {
                    name: searchTerm,
                    themeLayerId: this.$route.params.id ? this.$route.params.id : 0
                };
                const result = await this.$store.dispatch('useApiData', {
                    name: 'ThemeLayerExists',
                    data: parameters
                });
                if (result.data) {
                    this.stateValues.titleValidState = false;
                    this.stateValues.titleInvalidMessage =
                        this.$t('themelayerInUse');
                } else {
                    this.stateValues.titleValidState = true;
                    this.stateValues.titleInvalidMessage = null;
                }
            }, 750),

            /**
             * Checks if the given themelayer name already exists in the database
             * For NedGlobe Services (readonly) layers the name must be unique
             * @return {void} - Sets error message if needed
             */
            $_layerNameExists(layerNameValue) {
                const parameters = {
                    layerName: layerNameValue.value,
                    serviceType: layerNameValue.serviceType
                };

                this.$store
                    .dispatch('useApiData', {
                        name: 'GetLayerNameExists',
                        parameters
                    })
                    .then((result) => {
                        if (result.data) {
                            this.stateValues.nameValidState = false;
                            this.stateValues.nameInvalidMessage =
                                this.$t('themelayerInUse');
                        } else {
                            this.stateValues.nameValidState = true;
                            this.stateValues.nameInvalidMessage = null;
                        }
                    });
            },

            /**
             * Checks if the given themelayer name already exists in GeoServer
             * @return {void} - Sets error message if needed
             */
            async $_geoServerLayerNameExists(layerName) {
                if (!layerName || layerName.length === 0) {
                    return;
                }

                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetGeoServerLayerExist',
                    parameters: { LayerName: layerName }
                });
                if (result?.data) {
                    this.stateValues.nameValidState = false;
                    this.stateValues.nameInvalidMessage = this.$t(
                        'nedglobeServices.layerNameInUse'
                    );
                } else {
                    this.stateValues.nameValidState = true;
                    this.stateValues.nameInvalidMessage = null;
                }
            },

            /**
             * Checks availableTileMatrixSet and sets as default if one starts with EPSG:28992
             * Not equals but starts with because of BGT Achtergrondlaag WMTS ('EPSG:28992:16')
             */
            $_setDefaultMatrixSet() {
                if (this.selectedOwsServiceLayer?.availableTileMatrixSet) {
                    let done = false;
                    // Check the available matrixsets
                    this.selectedOwsServiceLayer.availableTileMatrixSet.forEach(
                        (matrixSet) => {
                            // If identifier starts with default name, then we set it as default
                            if (
                                !done &&
                                matrixSet.identifier.startsWith(PROJECTION_CODE)
                            ) {
                                this.selectedOwsServiceLayer.tileMatrixSet =
                                    matrixSet;
                                // Alternative break, in forEach
                                done = true;
                            }
                        }
                    );
                }
            },

            /**
             * Get service layers for layers combobox
             * Layername is not unique, therefore ServiceType is added to the value to make it unique
             * @return {array} array with layers
             */
            $_getLayerOptions(serviceType) {
                if (this.selectedOwsService?.layers) {
                    let filteredLayers = this.selectedOwsService.layers.filter(
                        (item) => item.serviceType === serviceType
                    );
                    filteredLayers = orderBy(filteredLayers, [
                        (o) => o.title.toLowerCase()
                    ]);
                    return filteredLayers.map((item) => {
                        return {
                            value: `${item.serviceType}:${item.layerName}`,
                            text: item.title
                        };
                    });
                }
                return [];
            },

            $_changeGraphicLayerTitle(layerTitle) {
                const filteredLayers = this.selectedOwsService.layers.filter(
                    (item) =>
                        item.serviceType === OWS_TYPE.WMS &&
                        item.title === this.propertyValues.layerCustomTitle
                );
                if (filteredLayers && filteredLayers.length === 1) {
                    filteredLayers[0].title = layerTitle;
                }
            },

            /***************************************************************************************************************
             **** Save/Cancel/Remove
             ****************************************************************************************************************/
            async $_onSubmit() {
                this.validationWarning = null;
                if (this.$refs.themeLayerForm.checkValidity()) {
                    if (
                        this.stateValues.valideImageState &&
                        this.stateValues.titleValidState &&
                        (await this.$_canSaveProperties()) &&
                        this.$_canSaveStyles() &&
                        this.$_canSaveRelations() &&
                        this.$_validGeometryType() &&
                        this.$_canSaveForm()
                    ) {
                        this.$_saveForm();
                    } else if (this.validationWarning !== null) {
                        const settings = {
                            message: this.validationWarning.message,
                            title: this.validationWarning.title,
                            okTitle: this.$t('btnSaveLabel'),
                            cancelTitle: this.$t('btnCancelLabel'),
                            noCloseOnBackdrop: true
                        };
                        this.msgBoxConfirm(settings).then((confirmed) => {
                            if (confirmed) {
                                this.$_saveForm();
                            }
                        });
                    }
                }
            },

            async $_saveLayerInNedGlobeServices(themeLayerId) {
                const action = this.isEditMode ? 'Update' : 'Create';
                const name = this.isNedGlobeServicesLayerSourceLufo
                    ? `${action}AerialPhotoLayer`
                    : `${action}PostGisLayer`;
                const body = {
                    themeLayerId: themeLayerId,
                    aerialPhotoName: this.isNedGlobeServicesLayerSourceLufo
                        ? this.layerSourceName
                        : null,
                    tableName: this.isNedGlobeServicesLayerSourceTable
                        ? this.layerSourceName
                        : null,
                    layerTitle: this.propertyValues.layerCustomTitle,
                    keywords: this.propertyValues.keywords,
                    layerName: this.propertyValues.layerName,
                    oldLayerName: this.isEditMode
                        ? this.editThemeLayer.layerName
                        : null,
                    newLayerName: this.isEditMode
                        ? this.propertyValues.layerName
                        : null
                };

                try {
                    const result = await this.$store.dispatch('useApiData', {
                        name,
                        useLoader: true,
                        parameters: {},
                        data: body
                    });

                    // if save was successful, set oldLayerName to the newLayerName
                    this.editThemeLayer.layerName = this.propertyValues.layerName;

                    this.showToast({
                        message: this.$t(
                            'nedglobeServices.LayerInformationMessage'
                        ),
                        variant: 'info',
                        autoHideDelay: 10000,
                        noCloseButton: true
                    });

                    this.hideToast();

                    return {
                        boundingBox: `${result.data.MinX},${result.data.MinY},${result.data.MaxX},${result.data.MaxY}`,
                        geometryColumn: result.data.GeometryColumn
                    };
                } catch (error) {
                    const messageData = {
                        title: this.$t('saveFailureTitle'),
                        message: this.$t(error.message),
                        variant: 'danger',
                        autoHideDelay: 10000
                    };
                    this.showToast(messageData);
                    return null;
                }
            },

            /**
             *  Check WFS and graphical layer before saving
             */
            $_canSaveForm() {
                let valideState = true;
                if (this.isOGCAPI) {
                    return true;
                }
                // Start by validating the form, to show possible error-messages
                if (this.selectedOwsServiceLayer) {
                    if (this.selectedOwsServiceLayer.serviceType === OWS_TYPE.WFS) {
                        valideState = this.$_validateWFSLayer();
                    }
                } else if (!this.isOwsNedGlobeServices) {
                    valideState = false;
                }
                return valideState;
            },
            $_validGeometryType() {
                // Check if geometryType is filled
                if (
                    this.isOwsNedGlobeServices &&
                    this.isNedGlobeServicesLayerSourceTable &&
                    this.geometryTypeArray.length === 0
                ) {
                    this.showToast({
                        message: this.$t('notFilledField', [
                            `${this.$t('geomType')}`
                        ]),
                        variant: 'danger',
                        autoHideDelay: 5000
                    });
                    return false;
                }
                return true;
            },
            $_canSaveStyles() {
                if (this.canHaveStyles) {
                    if (!this.$refs.stylesEdit.validateStyles()) {
                        this.showToast({
                            message: this.$t('styling.incorrectInputFields'),
                            variant: 'danger',
                            autoHideDelay: 5000
                        });
                        return false;
                    }
                }
                return true;
            },
            $_canSaveRelations() {
                if (this.canHaveRelations) {
                    const validRelations =
                        this.$refs.relationsEdit.validateRelations();

                    let validRelationsTable = true;
                    if (this.hasNedGlobeServicesOws) {
                        validRelationsTable =
                            this.$refs.relationsTableEdit.validateRelations();
                    }

                    if (
                        validRelations.length > 0 ||
                        validRelationsTable.length > 0
                    ) {
                        this.showToast({
                            message: this.$t('relations.missingAttributes', [
                                [...validRelations, ...validRelationsTable].join(
                                    '\n'
                                )
                            ]),
                            variant: 'danger',
                            autoHideDelay: 5000
                        });
                        return false;
                    }
                }
                return true;
            },
            async $_canSaveProperties() {
                const isValid =
                    await this.$refs.themeLayerProperties.validateProperties();
                if (!isValid) {
                    this.showToast({
                        message: this.canHaveStyles
                            ? this.$t('incorrectInputFieldsTabGeneral')
                            : this.$t('incorrectInputFields'),
                        variant: 'danger',
                        autoHideDelay: 5000
                    });
                    return false;
                }
                return true;
            },
            /**
             * Determines whether the defined graphical layer still available
             */
            $_validateGraphicalLayerAvailability() {
                this.unavailableGraphicalLayerMessage = '';
                // Check whether the WMS and WMTS uris are available
                this.$_validateGraphicalLayeAndOwsUriCombi();
                if (!this.glOwsCombiIsChecked) {
                    if (
                        this.comboBoxGraphicalLayers.length > 0 &&
                        this.editThemeLayer.graphicalLayerName
                    ) {
                        const notAvailable =
                            this.comboBoxGraphicalLayers.find(
                                (item) =>
                                    item.value.toLowerCase() ===
                                    this.editThemeLayer.graphicalLayerName.toLowerCase()
                            ) === undefined;
                        if (notAvailable) {
                            this.unavailableGraphicalLayerMessage = `${this.$t(
                                'graphicLayerUnavailableError',
                                [this.editThemeLayer.graphicalLayerName]
                            )} ${this.$t('chooseAvailableOption')}`;
                        }
                    } else if (this.editThemeLayer.graphicalLayerName) {
                        this.unavailableGraphicalLayerMessage = `${this.$t(
                            'graphicLayerUnavailableError',
                            [this.editThemeLayer.graphicalLayerName]
                        )} ${this.$t('layerListEmpty')}`;
                    } else {
                        this.$_checkWms_WMTSLayers();
                    }
                }
            },

            $_OwsHasOnlyUrlType(type) {
                let result = false;
                if (type === OWS_TYPE.WMTS) {
                    result =
                        this.selectedOwsService.uri_WMTS?.length > 0 &&
                        (this.selectedOwsService.uri_WMS === null ||
                            this.selectedOwsService.uri_WMS?.length <= 0) &&
                        (this.selectedOwsService.uri_WFS === null ||
                            this.selectedOwsService.uri_WFS?.length <= 0);
                } else if (type === OWS_TYPE.WFS) {
                    result =
                        this.selectedOwsService.uri_WFS?.length > 0 &&
                        (this.selectedOwsService.uri_WMS === null ||
                            this.selectedOwsService.uri_WMS?.length <= 0) &&
                        (this.selectedOwsService.uri_WMTS === null ||
                            this.selectedOwsService.uri_WMTS?.length <= 0);
                }
                return result;
            },

            $_checkWms_WMTSLayers() {
                const owsServiceSelected = Object.prototype.hasOwnProperty.call(
                    this.selectedOwsService,
                    'name'
                );
                this.isOWS_WFS = false;

                if (owsServiceSelected) {
                    if (this.$_OwsHasOnlyUrlType(OWS_TYPE.WFS)) {
                        this.unavailableGraphicalLayerMessage = '';
                        this.isOWS_WFS = true;
                    } else if (this.$_OwsHasOnlyUrlType(OWS_TYPE.WMTS)) {
                        this.unavailableGraphicalLayerMessage =
                            this.comboBoxOwsServiceWmtsLayers.length > 0
                                ? ''
                                : `${this.$t('layerListEmpty')}`;
                    }
                }
            },

            /*
             * Checks the combination of graphicalLayer and OWS uri_WMS, uri_WMTS
             */
            $_validateGraphicalLayeAndOwsUriCombi() {
                if (
                    this.editThemeLayer.graphicalLayerName &&
                    !this.selectedOwsService.uri_WMS &&
                    !this.selectedOwsService.uri_WMTS &&
                    !this.glOwsCombiIsChecked
                ) {
                    this.glOwsCombiIsChecked = true;
                    this.showToast({
                        message: this.$t('graphicLayerOwsUriError'),
                        variant: 'danger',
                        autoHideDelay: 5000,
                        noCloseButton: true
                    });
                }
            },

            $_validateWFSLayer() {
                // Show warning when another WFS layer is choosen and the layer no longer exists in the OWS GetCapabilities
                if (
                    !this.isOwsNedGlobeServices &&
                    this.mode === MODE_EDIT &&
                    this.editThemeLayer.layerName !==
                        this.selectedOwsServiceLayer.layerName &&
                    this.showPopupLayerAdjusted
                ) {
                    this.showPopupLayerAdjusted = false;
                    this.validationWarning = {
                        message: this.$t('layerAdjustedWarning'),
                        title: this.$t('warning')
                    };
                }

                // Don't validate graphicallayer when there is none available or set
                if (
                    !this.glOwsCombiIsChecked &&
                    this.comboBoxGraphicalLayers.length > 0
                ) {
                    // check graphicalLayer
                    if (!this.graphicalLayer || !this.graphicalLayer.layerName) {
                        this.validationWarning = {
                            message: this.$t('graphicLayerEmptyWarning'),
                            title: this.$t('warning')
                        };
                    } else if (
                        // Check if graphical layer name not equals WFS name
                        !this.$_layernamesAreEqual(
                            this.selectedOwsServiceLayer.layerName,
                            this.graphicalLayer.layerName
                        ) &&
                        // and if graphical layer title not equals WFS title
                        !this.$_layernamesAreEqual(
                            this.selectedOwsServiceLayer.title,
                            this.graphicalLayer.title
                        )
                    ) {
                        // Then give a warning that maybe the user picked the wrong graphical layer
                        this.validationWarning = {
                            message: this.$t('themelayerWarningGraphicLayer', [
                                this.graphicalLayer.layerName,
                                this.selectedOwsServiceLayer.layerName
                            ]),
                            title: this.$t('warning')
                        };
                    }
                }
                return this.validationWarning === null;
            },

            $_layernamesAreEqual(layername, graphname) {
                // strip namespace
                const layernameStripped =
                    this.$_getLayernameWithoutNamespace(layername);
                const graphnameStripped =
                    this.$_getLayernameWithoutNamespace(graphname);

                if (
                    layernameStripped.localeCompare(graphnameStripped, undefined, {
                        sensitivity: 'base'
                    }) === 0
                ) {
                    return true;
                }

                // do a more fuzzy match.
                // F.i. name: <Name>PS.ProtectedSite</Name> equals name: <Name>pv-ps:PS_ProtectedSite</Name>
                const layernameStrippedFuzzy =
                    this.$_getFuzzyLayerName(layernameStripped);
                const graphnameStrippedFuzzy =
                    this.$_getFuzzyLayerName(graphnameStripped);

                if (
                    layernameStrippedFuzzy.localeCompare(
                        graphnameStrippedFuzzy,
                        undefined,
                        { sensitivity: 'base' }
                    ) === 0
                ) {
                    return true;
                }

                // Compare names by replacing _ with space
                const layernameStrippedUnderscore =
                    this.$_getLayerNameNoUnderscore(layernameStripped);
                const graphnameStrippedUnderscore =
                    this.$_getLayerNameNoUnderscore(graphnameStripped);
                return (
                    layernameStrippedUnderscore.localeCompare(
                        graphnameStrippedUnderscore,
                        undefined,
                        { sensitivity: 'base' }
                    ) === 0
                );
            },

            $_getLayernameWithoutNamespace(layername) {
                if (layername.indexOf(':') > -1) {
                    return layername.split(':')[1];
                } else {
                    return layername;
                }
            },

            /**
             * In some cases the layername differs between the layername and the graphical layername.
             * Found cases are a underscore and point differences.
             */
            $_getFuzzyLayerName(layername) {
                return layername.replaceAll('.', '_');
                // Add more differences if needed ...
            },

            /**
             * In some cases the layername differs between the layername and the graphical layername.
             */
            $_getLayerNameNoUnderscore(layername) {
                return layername.replaceAll('_', ' ');
            },

            async $_getThemeLayer() {
                // clone selected ows layer
                const themeLayer = { ...this.selectedOwsServiceLayer };

                if (this.conversionToNedGlobeServices) {
                    themeLayer.id = this.editThemeLayer.id;
                }

                if (this.isOGCAPI) {
                    themeLayer.serviceType = OWS_TYPE.OGCT;
                    themeLayer.sourceId = this.selectedOwsService.id;
                    themeLayer.id = this.editThemeLayer.id;
                }

                if (this.isOwsNedGlobeServices) {
                    const result = await this.$_saveLayerInNedGlobeServices(
                        themeLayer.id
                    );
                    if (!result) {
                        nprogress.done();
                        this.isSaving = false;
                        return null;
                    }

                    themeLayer.boundingBox = result.boundingBox;
                    themeLayer.geometryColumn = result.geometryColumn;

                    themeLayer.sourceId = this.selectedOwsService.id;

                    themeLayer.serviceType = this.isNedGlobeServicesLayerSourceLufo
                        ? OWS_TYPE.WMS
                        : OWS_TYPE.WFS;

                    themeLayer.tiledRequest = null;
                    themeLayer.layerSourceType = this.layerSourceType;
                    themeLayer.layerSourceName = this.layerSourceName;
                    themeLayer.tiledRequest = false;
                    themeLayer.geometryType = this.propertyValues.geometryType;
                    themeLayer.autoOpenAllRelationsTabs =
                        this.autoOpenAllRelationsTabs;
                } else if (this.selectedOwsServiceLayer) {
                    themeLayer.tiledRequest =
                        this.selectedOwsServiceLayer.tiledRequest;
                }

                // Properties
                themeLayer.isPubliclyAccessible =
                    this.propertyValues.isPubliclyAccessible;
                themeLayer.wmtsStyle = this.propertyValues.wmtsStyle;

                themeLayer.availableTileMatrixSet = null;
                themeLayer.tileMatrixSet = this.propertyValues.tileMatrixSet;

                themeLayer.tolerance = this.propertyValues.toleranceType
                    ? this.propertyValues.toleranceType.tolerance
                    : this.propertyValues.toleranceType;
                themeLayer.selectionAttribute =
                    this.propertyValues.selectionAttribute;
                themeLayer.symbolShiftX = this.propertyValues.symbolShiftX;
                themeLayer.symbolShiftY = this.propertyValues.symbolShiftY;

                // Remove whitespace from the start and end of the title
                themeLayer.title = this.propertyValues.layerCustomTitle.trim();

                themeLayer.layerName = this.isOwsNedGlobeServices
                    ? `${this.workspaceName}${this.propertyValues.layerName}`
                    : themeLayer.layerName;

                themeLayer.format = this.propertyValues.format;

                themeLayer.description = this.propertyValues.description;
                themeLayer.mapTerms = this.propertyValues.mapTerms;
                themeLayer.keywords = this.propertyValues.keywords;
                themeLayer.metadataurl = this.propertyValues.metadataUrl;
                themeLayer.infoUrl = this.propertyValues.infoUrl;
                themeLayer.infoLinkType = this.propertyValues.infoLinkType;

                themeLayer.baselayer = this.propertyValues.baselayer;
                themeLayer.baselayerImage = this.propertyValues.baselayerImage;
                themeLayer.minBaseLayerResolution =
                    this.propertyValues.minBaseLayerResolution;
                themeLayer.maxBaseLayerResolution =
                    this.propertyValues.maxBaseLayerResolution;

                // graphicalLayerName
                if (
                    this.isOwsNedGlobeServices &&
                    (!this.isEditMode ||
                        this.editThemeLayer.geoServerLayerName ===
                            this.graphicalLayer?.graphicalLayerName)
                ) {
                    themeLayer.graphicalLayerName = themeLayer.layerName;
                } else {
                    themeLayer.graphicalLayerName =
                        this.graphicalLayer?.graphicalLayerName;
                }
                return themeLayer;
            },

            /**
             * Save current changes
             */
            async $_saveForm() {
                nprogress.start();
                this.isSaving = true;
                const themeLayer = await this.$_getThemeLayer();

                if (!themeLayer) {
                    return;
                }
                try {
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'SaveThemeLayer',
                        data: themeLayer
                    });
                    if (this.canHaveRelations) {
                        await this.$refs.relationsEdit.saveRelations();
                        this.$refs.relationsEdit.loadRelations();

                        if (this.hasNedGlobeServicesOws) {
                            await this.$refs.relationsTableEdit.saveTableRelations();
                            this.$refs.relationsTableEdit.loadTableRelations();
                        }
                    }
                    if (this.canHaveStyles) {
                        await this.$refs.stylesEdit.saveStyles();
                        this.$refs.stylesEdit.loadStyles();
                    }
                    if (themeLayer.id && themeLayer.id > 0) {
                        this.$_removeLayerFromCache(themeLayer.id);
                    }

                    // show table columns that are restricted GeoServer names
                    await this.$_showGeoServerRestrictedNames(themeLayer);

                    nprogress.done();
                    this.isSaving = false;
                    this.showToast({
                        message: this.$t('themelayerCardSavedConfirmation'),
                        variant: 'success',
                        autoHideDelay: 2500,
                        noCloseButton: true
                    });
                    // if new themelayer, goto overview
                    if (this.mode === MODE_ADD) {
                        this.$_gotoOverview();
                    } else {
                        // set the unavailable flag from the result
                        this.editThemeLayer.unavailable = result.data.unavailable;

                        if (this.conversionToNedGlobeServices) {
                            // reload to get the style tab
                            setTimeout(() => this.$router.go(), 500);
                        }
                    }
                } catch (error) {
                    nprogress.done();
                    this.isSaving = false;
                    this.showToast({
                        title: this.$t('saveFailureTitle'),
                        message: this.$t(error.message),
                        variant: 'danger',
                        autoHideDelay: 10000
                    });
                }
            },

            /**
             * Cancel current modificatiosn to the OWS
             * and go back to the overview
             */
            $_onCancel() {
                this.$_gotoOverview();
            },

            /**
             * Remove current themelayer
             * and go back to the overview
             */
            async $_onRemove() {
                const confirm = await this.msgBoxConfirm({
                    message: this.$t('themeLayerCardDeletionsConfirmation')
                });
                if (confirm) {
                    nprogress.start();
                    this.isDeleting = true;
                    try {
                        // remove layer in sql database
                        await this.$store.dispatch('useApiData', {
                            name: 'DeleteThemeLayer',
                            parameters: {
                                themeLayerId: this.editThemeLayer.id
                            }
                        });

                        // remove table in GeoServer
                        if (this.isNedGlobeServicesLayerSourceTable) {
                            await this.$store.dispatch('useApiData', {
                                name: 'DeletePostGisLayer',
                                data: { layerName: this.propertyValues.layerName }
                            });
                        }

                        // remove lufo in GeoServer
                        if (this.isNedGlobeServicesLayerSourceLufo) {
                            await this.$store.dispatch('useApiData', {
                                name: 'DeleteAerialPhotoLayer',
                                data: {
                                    layerName: this.propertyValues.layerName,
                                    aerialPhotoName: this.layerSourceName
                                }
                            });
                        }

                        // remove styles in GeoServer
                        if (this.canHaveStyles) {
                            await this.$refs.stylesEdit.removeStyles();
                        }

                        this.isDeleting = false;
                        nprogress.done();
                        this.showToast({
                            message: this.$t('themeLayerRemoved'),
                            variant: 'success',
                            autoHideDelay: 5000
                        });
                        this.$_gotoOverview();
                    } catch (error) {
                        this.showToast({
                            title: this.$t('deleteFailureTitle'),
                            message: this.$t('deleteFailureMessage', [
                                `${error.response.data.message}`
                            ]),
                            variant: 'danger',
                            autoHideDelay: 10000
                        });
                    } finally {
                        nprogress.done();
                        this.isDeleting = false;
                    }
                }
            },

            /**
             * Goto the overview form
             */
            $_gotoOverview() {
                this.$router.push('/dashboard/themelayers');
            }
        }
    };
</script>

<style lang="scss">
.hide-tabs {
    & .nav.nav-tabs {
        display: none;
    }

    & .tab-content {
        margin-left: -15px;
    }
}
</style>
<style scoped lang="scss">
#convert-button-container {
    margin-bottom: 15px;
}

.error {
    color: red;
}

/* Make sure there is enough space for colorpicker */
.extra-padding-bottom {
    padding-bottom: 200px;
}

.label-transactional {
    padding-bottom: 5px;
    font-size: 18px;
    font-style: italic;
    font-weight: bold;
}
</style>
